import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { type InjectedFormProps, SubmissionError, reduxForm } from "redux-form";

import { ActionButton, Callout, CalloutIntent, Card, CardGrid, CardGridArea, CardRowSimple } from "widgets";

import styleLayout from "./styles/PageLayout.module.scss";

import { simpleFieldValidation } from "../../app-utils/form.js";
import { type OraclePasswordLimit } from "../../graphql.js";
import { changeYourPassword } from "../../redux/modules/auth.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import {
  CreateAccountPasswordFormFields,
  type CreateAccountPasswordFormFieldsProps,
  CreatePasswordFormNames,
  validateAccountPasswordFields,
} from "./CreateAccountPasswordPage.js";
import { PasswordInputField } from "./redux-form/InputField.js";

const AccountSettingsPasswordForm = (props: CreateAccountPasswordFormFieldsProps & InjectedFormProps<any, any>) => {
  const { handleSubmit, submitting, error } = props;

  const dispatch = useDispatch<ReduxDispatch>();

  function onChangePassword(values) {
    const payload = {
      oldPassword: values[CreatePasswordFormNames.FIELD_OLD_PASS],
      newPassword: values[CreatePasswordFormNames.FIELD_PASS],
    };

    return dispatch(changeYourPassword(payload)).catch((rejected: Error) => {
      throw new SubmissionError({ _error: rejected.message });
    });
  }

  return (
    <Card key="account-settings-overview-card" title="Password Modification" area={CardGridArea.Left}>
      <CardRowSimple>Change your account password by providing the information below</CardRowSimple>
      {/* autocomplete feature in some browsers can consider the input field before a password field */}
      {/* Browsers are aware of the CSS "visibility", CSS "display" and "hidden" attribute trick */}
      <input
        autoComplete="username"
        tabIndex={-1}
        style={{ position: "fixed", pointerEvents: "none", top: -10000, left: -10000, opacity: 0, zIndex: -1 }}
        aria-hidden={true}
      />
      <PasswordInputField
        label={"Current password"}
        name={CreatePasswordFormNames.FIELD_OLD_PASS}
        placeholder="Current password"
        validate={simpleFieldValidation}
        // avoid allowReadability and keep autocomplete off
        autocomplete={"new-password"}
      />
      <CreateAccountPasswordFormFields passwordRules={props.passwordRules} />
      <Callout intent={CalloutIntent.Danger}>{error}</Callout>
      <ActionButton
        isLoading={submitting}
        onPress={handleSubmit(onChangePassword)}
        type={"submit"}
        label={"Change Password"}
      />
    </Card>
  );
};

const AccountSettingsPasswordCard = reduxForm({
  form: CreatePasswordFormNames.FORM_NAME,
  validate: validateAccountPasswordFields,
})(AccountSettingsPasswordForm) as any;

const AccountSettingsPasswordPage = () => {
  // passwordRules need to be passed through props so that they are injected in the redux form validation static function
  const passwordRules = useSelector<IAppState, OraclePasswordLimit>(
    (state) => state.config.configuration.oraclePasswordLimits,
    shallowEqual,
  );

  return (
    <CardGrid className={styleLayout.page}>
      <AccountSettingsPasswordCard passwordRules={passwordRules} />
    </CardGrid>
  );
};

export default AccountSettingsPasswordPage;
